import { isAcTerminalId } from "./ac";
import type { SimulateContext } from "./cable-current";
import type { DefLookup } from "./catalog";
import { isInverterCharger } from "./component-ports";
import { effectiveTerminals } from "./effective";
import { matchesAcSafetyIdentity } from "./diagnostics/ac-safety-identity";
import { buildGraph, computeActiveSourceCircuits, isDcSideSupplied, v } from "./diagnostics/graph";
import { getEffectiveStateOption, getSimulateStateSpec } from "./simulate/states";
import type { Cable, ComponentDefinition, ComponentNode, System, Terminal } from "./types";

export type AcSupplyStatus = "supplied" | "off" | "unverified";
type Pole = "line" | "neutral" | "earth";
type Part = { node: ComponentNode; def: ComponentDefinition; terms: Terminal[] };
type Port = { part: Part; line: string; neutral: string; lineId: string };
type Supply = { line: Set<string>; neutral: Set<string>; origin: string };
export type AcAvailability = {
  input: ReadonlyMap<string, AcSupplyStatus>;
  output: ReadonlyMap<string, AcSupplyStatus>;
  cables: ReadonlyMap<string, AcSupplyStatus>;
};

const pole = (term: Terminal): Pole | undefined => {
  if (term.polarity === "ground" || /[-_]pe$/i.test(term.id)) return "earth";
  if (!isAcTerminalId(term.id)) return undefined;
  if (/[-_]l[12]?$/i.test(term.id)) return "line";
  if (/[-_]n$/i.test(term.id)) return "neutral";
  return undefined;
};
const stem = (id: string) => id.replace(/[-_](?:l[12]?|n)$/i, "").toLowerCase();
const ports = (part: Part, direction: "input" | "output"): Port[] => part.terms
  .filter(t => t.direction === direction && pole(t) === "line")
  .flatMap(line => {
    const neutral = part.terms.find(t => pole(t) === "neutral" && stem(t.id) === stem(line.id));
    return neutral ? [{ part, line: v(part.node.id, line.id), neutral: v(part.node.id, neutral.id), lineId: line.id }] : [];
  });
const isMagnum = (def: ComponentDefinition) => ["shore-magnum-msh3012rv", "shore-magnum-msh3012rv-l"].includes(def.id);
const isTransformer = (def: ComponentDefinition) => def.id === "shore-victron-isolation-transformer-3600";
const cache = new WeakMap<System, WeakMap<DefLookup, WeakMap<object, WeakMap<object, AcAvailability>>>>();
const EMPTY_LOADS = {};

/** Source/contact availability only: no RMS current, measured voltage, phase
 * qualification, protective trips or safe-isolation claim. Line and neutral
 * must reach the same source port; PE never proves a usable supply. */
export function computeAcAvailability(system: System, lookup: DefLookup, simulate: SimulateContext): AcAvailability {
  let byLookup = cache.get(system); if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  let byState = byLookup.get(lookup); if (!byState) { byState = new WeakMap(); byLookup.set(lookup, byState); }
  let byLoads = byState.get(simulate.componentStates); if (!byLoads) { byLoads = new WeakMap(); byState.set(simulate.componentStates, byLoads); }
  const loads = simulate.loadStates ?? EMPTY_LOADS;
  const cached = byLoads.get(loads); if (cached) return cached;
  const parts: Part[] = system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    return def ? [{ node, def, terms: effectiveTerminals(def, node) }] : [];
  });
  const endpoints = new Map(parts.flatMap(part => part.terms.map(term => [v(part.node.id, term.id), { part, term }] as const)));
  const graph = buildGraph(system, lookup, { simulate, excludeSense: true });
  const dcSources = computeActiveSourceCircuits(system, lookup, simulate);
  const invalidState = (part: Part) => getSimulateStateSpec(part.def) != null &&
    getEffectiveStateOption(part.def, part.node.id, simulate) == null;
  const crossed = new Set<string>();
  const walk = (start: string, kind: "line" | "neutral", uncertain = false) => {
    const seen = new Set<string>();
    const queue = [start];
    for (let i = 0; i < queue.length; i++) {
      const vertex = queue[i];
      if (seen.has(vertex)) continue;
      const endpoint = endpoints.get(vertex);
      if (!endpoint) continue;
      const neutralPost = endpoint.part.def.category === "distribution" &&
        !isAcTerminalId(endpoint.term.id) && endpoint.term.polarity === "neutral";
      const unknownAcPole = uncertain && isAcTerminalId(endpoint.term.id) && pole(endpoint.term) == null;
      if (pole(endpoint.term) !== kind && !neutralPost && !unknownAcPole) {
        if ((!uncertain || vertex !== start) && ["line", "neutral"].includes(pole(endpoint.term) ?? "")) crossed.add(vertex);
        continue;
      }
      seen.add(vertex);
      for (const edge of graph.adj.get(vertex) ?? []) {
        // Converter/transformer stages are not copper connections. Only
        // declared passive contacts may carry source provenance through.
        if (edge.via.kind === "intra" && (endpoint.part.def.category !== "distribution" ||
            isTransformer(endpoint.part.def) || invalidState(endpoint.part))) continue;
        queue.push(edge.vertex);
      }
    }
    return seen;
  };
  const ambiguousPaths = () => {
    const ambiguous = new Set<string>();
    const queue = [...crossed];
    for (let i = 0; i < queue.length; i++) {
      const vertex = queue[i], endpoint = endpoints.get(vertex);
      if (ambiguous.has(vertex) || !endpoint || pole(endpoint.term) === "earth" ||
          (!isAcTerminalId(endpoint.term.id) && endpoint.term.polarity !== "neutral")) continue;
      ambiguous.add(vertex);
      for (const edge of graph.adj.get(vertex) ?? []) {
        if (edge.via.kind === "intra" && (endpoint.part.def.category !== "distribution" ||
            isTransformer(endpoint.part.def) || invalidState(endpoint.part))) continue;
        queue.push(edge.vertex);
      }
    }
    return ambiguous;
  };
  const supplies: Supply[] = [];
  const unknown: Supply[] = [];
  const addSupply = (port: Port, origin: string, target = supplies) => {
    const line = walk(port.line, "line"), neutral = walk(port.neutral, "neutral");
    if ([...line].some(vertex => neutral.has(vertex))) {
      unknown.push({ line, neutral, origin });
    } else target.push({ line, neutral, origin });
  };
  const matches = (port: Port) => supplies.filter(s => s.line.has(port.line) && s.neutral.has(port.neutral));
  const sourceNode = (supply: Supply) => endpoints.get(supply.origin)?.part.node.id ?? supply.origin;
  const stateOn = (part: Part) => {
    const option = getEffectiveStateOption(part.def, part.node.id, simulate);
    if (getSimulateStateSpec(part.def) && !option) return undefined;
    return option?.id !== "off" && option?.ampOverride !== 0 && option?.ampMultiplier !== 0;
  };
  const acOutputs = (part: Part) => part.terms.filter(term => term.direction === "output" && isAcTerminalId(term.id) && pole(term) !== "earth");
  const addUnknownOutputs = (part: Part, unpairedOnly = false) => {
    const paired = new Set(ports(part, "output").flatMap(port => [port.line, port.neutral]));
    for (const term of acOutputs(part)) {
      const vertex = v(part.node.id, term.id);
      if (!unpairedOnly || !paired.has(vertex)) unknown.push({
        line: walk(vertex, "line", true), neutral: walk(vertex, "neutral", true), origin: vertex,
      });
    }
  };
  for (const part of parts) {
    const outputs = ports(part, "output");
    if (part.def.category === "source" && stateOn(part) !== false) {
      addUnknownOutputs(part, true);
    }
    if (part.def.category === "source") {
      const on = stateOn(part);
      for (const port of outputs) {
        if (on) addSupply(port, port.line);
        else if (on == null) addSupply(port, port.line, unknown);
      }
    } else if (part.def.category === "load" || part.def.category === "charger") {
      const option = getEffectiveStateOption(part.def, part.node.id, simulate);
      const inverting = isInverterCharger(part.def) ? option?.role === "draw" : stateOn(part);
      if (inverting && isDcSideSupplied(part.node, part.def, "input", dcSources)) {
        for (const port of outputs) addSupply(port, port.line);
        addUnknownOutputs(part, true);
      } else if (stateOn(part) == null) {
        addUnknownOutputs(part);
      }
    }
  }
  // Magnum Rev E §§2.9, 3.1–3.3: qualified shore AC passes through with
  // the inverter Off. HOT1 alone feeds both outputs; dual input keeps legs
  // separate. Only the reviewed electrical identity inherits this behavior.
  // https://www.sensata.com/sites/default/files/a/sensata-msh-3012rv-inverter-charger-manual.pdf
  const transfers = parts.filter(part => part.terms.some(t => t.direction === "input" && isAcTerminalId(t.id)) && acOutputs(part).length &&
    (part.def.category !== "distribution" || isTransformer(part.def) || invalidState(part)));
  const transferred = new Set<string>();
  const uncertainTransfers = new Set<string>();
  for (let pass = 0; pass < transfers.length; pass++) {
    let changed = false;
    for (const part of transfers) {
      if (transferred.has(part.node.id)) continue;
      const inputs = ports(part, "input");
      const inputLive = inputs.some(input => matches(input).length);
      const inputVertices = part.terms.filter(t => t.direction === "input" && isAcTerminalId(t.id)).map(t => v(part.node.id, t.id));
      const ambiguous = ambiguousPaths();
      const inputUncertain = inputVertices.some(vertex => ambiguous.has(vertex) ||
        unknown.some(s => s.line.has(vertex) || s.neutral.has(vertex)));
      const sourceConflict = new Set(inputs.flatMap(matches).map(sourceNode)).size > 1;
      const inputUnverified = inputUncertain || sourceConflict || inputVertices.some(vertex => supplies.some(s => s.line.has(vertex)));
      if (!inputLive && !inputUnverified) continue;
      const outputs = ports(part, "output");
      if (!inputLive || inputUncertain || sourceConflict || !isMagnum(part.def) || !matchesAcSafetyIdentity(part.node, part.def)) {
        addUnknownOutputs(part);
        uncertainTransfers.add(part.node.id);
        transferred.add(part.node.id);
        changed = true;
        continue;
      }
      const hot1 = inputs.find(input => input.lineId === "ac-in-l1")!;
      const hot2 = inputs.find(input => input.lineId === "ac-in-l2")!;
      const hot2Wired = system.cables.some(c => v(c.fromNodeId, c.fromTerminalId) === hot2.line || v(c.toNodeId, c.toTerminalId) === hot2.line);
      for (const output of outputs) {
        const input = output.lineId === "ac-out-l2" && hot2Wired ? hot2 : hot1;
        const inputSupplies = matches(input);
        if (!inputSupplies.length && inputUnverified) addSupply(output, part.node.id, unknown);
        for (const supply of inputSupplies) {
          // Do not turn transfer loops into a new independent source.
          addSupply(output, supply.origin);
          changed = true;
        }
      }
      transferred.add(part.node.id);
    }
    if (!changed) break;
  }
  // A later transfer can introduce another source or uncertainty at an input
  // already visited above. Propagate uncertainty to a fixed point, without
  // creating new sources or depending on component order.
  for (let pass = 0; pass < transfers.length; pass++) {
    let changed = false;
    const ambiguous = ambiguousPaths();
    for (const part of transfers) {
      if (uncertainTransfers.has(part.node.id)) continue;
      const inputs = ports(part, "input");
      const inputVertices = part.terms.filter(t => t.direction === "input" && isAcTerminalId(t.id)).map(t => v(part.node.id, t.id));
      const uncertain = inputVertices.some(vertex => ambiguous.has(vertex) ||
        unknown.some(s => s.line.has(vertex) || s.neutral.has(vertex)));
      const conflict = new Set(inputs.flatMap(matches).map(sourceNode)).size > 1;
      if (!uncertain && !conflict) continue;
      addUnknownOutputs(part);
      uncertainTransfers.add(part.node.id);
      changed = true;
    }
    if (!changed) break;
  }
  // A crossed L/N path is electrically ambiguous, not evidence of an off
  // circuit. Carry that ambiguity through passive contacts to all consumers.
  const ambiguous = ambiguousPaths();
  const status = (port: Port): AcSupplyStatus => {
    if (ambiguous.has(port.line) || ambiguous.has(port.neutral) ||
        unknown.some(s => s.line.has(port.line) || s.neutral.has(port.neutral))) return "unverified";
    const candidates = matches(port);
    if (new Set(candidates.map(s => s.origin)).size > 1) return "unverified";
    if (candidates.length) return "supplied";
    if (supplies.some(s => s.line.has(port.line))) return "unverified";
    return "off";
  };
  const grouped = (direction: "input" | "output") => new Map(parts.flatMap(part => {
    const partPorts = ports(part, direction);
    const values = partPorts.map(status);
    if (!values.length) return [];
    const conflict = new Set(partPorts.flatMap(matches).map(sourceNode)).size > 1;
    return [[part.node.id, conflict ? "unverified" : values.every(value => value === "supplied") ? "supplied" :
      values.every(value => value === "off") ? "off" : "unverified"] as const];
  }));
  const cables = new Map<string, AcSupplyStatus>();
  for (const cable of system.cables) {
    const ends = [v(cable.fromNodeId, cable.fromTerminalId), v(cable.toNodeId, cable.toTerminalId)];
    const acEnds = ends.map(vertex => ({ vertex, endpoint: endpoints.get(vertex) })).filter(end => end.endpoint && isAcTerminalId(end.endpoint.term.id));
    if (!acEnds.length || acEnds.every(end => pole(end.endpoint!.term) === "earth")) continue;
    const kinds = new Set(acEnds.map(end => pole(end.endpoint!.term)));
    if (kinds.has(undefined) || kinds.size !== 1 || ends.some(vertex => !endpoints.has(vertex) || ambiguous.has(vertex))) { cables.set(cable.id, "unverified"); continue; }
    const kind = kinds.has("line") ? "line" : "neutral";
    // A source-connected line is still live with a missing load neutral;
    // never call that conductor off just because a complete load isn't proven.
    const active = supplies.filter(s => ends.some(vertex => s[kind].has(vertex)));
    const unresolved = unknown.some(s => ends.some(vertex => s[kind].has(vertex)));
    // A split-phase source shares its neutral, but independent sources (or
    // line ports shorted together) have no verified phase relationship.
    const conflict = new Set(active.map(kind === "line" ? s => s.origin : sourceNode)).size > 1;
    cables.set(cable.id, unresolved || conflict ? "unverified" : active.length ? "supplied" : "off");
  }
  const result = { input: grouped("input"), output: grouped("output"), cables };
  byLoads.set(loads, result);
  return result;
}

export function acCableSupplyStatus(cable: Pick<Cable, "id">, availability: AcAvailability): AcSupplyStatus | undefined {
  return availability.cables.get(cable.id);
}
